import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import { useSnackbar } from 'notistack';

import { Box, Button } from '@mui/material';

import FlatImageEditor from '~/components/Common/Images/FlatImageEditor';
import {
  mergeFlatItemsWithLegacy,
  sortImagesByOrder,
  toFlatImageItem,
} from '~/components/Common/Images/legacyImageAdapter';
import {
  FlatImageEditorHandle,
  FlatImageEditorPendingUploadState,
  FlatImageItem,
  FlatImageUploadPayload,
} from '~/components/Common/Images/types';

import { getUploadedImageId, uploadImage } from '~/services/ImageService';

import getErrorMessage from '~/utils/getErrorMessage';

import { reportError } from '../../../utils/reportError';

import { buttonMessages as baseButtonMessages, buttonStates } from './states/submitButton';

const buttonMessages = { ...baseButtonMessages };
buttonMessages[buttonStates.default] = 'Save changes';

interface Image {
  id: string | number;
  publicImageId?: string;
  id_cloudinary_external?: string;
  imageId?: string;
  order?: number;
  title?: string;
  hidden?: boolean;
  filename?: string;
  is_hero?: boolean;
  source?: string;
  description?: string;
}

interface Props {
  images: Array<Image>;
  onAddImage: (cloudinaryId: string, filename: string, desiredOrder?: number) => Promise<unknown>;
  onDeleteImage?: (imageId: string | number) => Promise<unknown> | unknown;
  onUpdateImages: (images: Array<Image>) => Promise<unknown> | unknown;
  enablePendingCropUpload?: boolean;
  hasHiddenToggle?: boolean;
  hideDeleteButton?: boolean;
  onPendingUploadStateChange?: (state: FlatImageEditorPendingUploadState) => void;
  showSaveButton?: boolean;
}

const ImagesForm = forwardRef(function ImagesForm(props: Props, ref) {
  const {
    enablePendingCropUpload = false,
    hasHiddenToggle,
    hideDeleteButton = false,
    images: incomingImages,
    onAddImage,
    onDeleteImage,
    onPendingUploadStateChange,
    onUpdateImages,
    showSaveButton,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [saveButtonState, setSaveButtonState] = useState(buttonStates.default);
  const [images, setImages] = useState<Array<Image>>([]);
  const imagesRef = useRef<Array<Image>>([]);
  const imageEditorRef = useRef<FlatImageEditorHandle | null>(null);

  useEffect(() => {
    const sortedImages = sortImagesByOrder(incomingImages);
    imagesRef.current = sortedImages;
    setImages(sortedImages);
  }, [incomingImages]);

  const shouldShowSaveButton = showSaveButton !== undefined ? showSaveButton : true;
  const isButtonStateSaving = saveButtonState === buttonStates.saving;

  const debouncedPersistImages = useMemo(
    () =>
      debounce((nextImages: Array<Image>) => {
        Promise.resolve(onUpdateImages(nextImages)).catch(() => {
          setSaveButtonState(buttonStates.failed);
        });
      }, 1000),
    [onUpdateImages],
  );

  useEffect(() => {
    return () => {
      debouncedPersistImages.cancel();
    };
  }, [debouncedPersistImages]);

  useImperativeHandle(ref, () => ({
    addImageToList(newImages: Array<Image>) {
      const combinedImages = sortImagesByOrder(images.concat(newImages));
      imagesRef.current = combinedImages;
      setImages(combinedImages);
      onUpdateImages(combinedImages);
    },
    uploadAllPending() {
      return (
        imageEditorRef.current?.uploadAllPending?.() ??
        Promise.resolve({
          uploadedCount: 0,
          failedCount: 0,
          failedItems: [],
        })
      );
    },
  }));

  const flatItems = useMemo(() => images.map((image) => toFlatImageItem(image)), [images]);

  const handleItemsChange = (nextItems: Array<FlatImageItem>) => {
    if (saveButtonState !== buttonStates.default) {
      setSaveButtonState(buttonStates.default);
    }

    const currentImages = imagesRef.current;
    const nextImages = mergeFlatItemsWithLegacy(currentImages, nextItems);
    imagesRef.current = nextImages;
    setImages(nextImages);
    debouncedPersistImages(nextImages);
  };

  const handleUploadFile = async ({ description, desiredOrder, file, filename }: FlatImageUploadPayload) => {
    try {
      const uploadResponse = await uploadImage(file);
      const uploadedImageId = getUploadedImageId(uploadResponse);

      if (!uploadedImageId) {
        throw new Error('Image service did not return an image id');
      }

      const response = (await onAddImage(uploadedImageId, filename, desiredOrder)) as Partial<Image>;
      const currentImageCount = imagesRef.current.length;
      return toFlatImageItem({
        ...response,
        id: response.id ?? uploadedImageId,
        filename: response.filename ?? filename,
        order: response.order ?? desiredOrder ?? currentImageCount + 1,
        publicImageId: response.publicImageId ?? response.id_cloudinary_external ?? response.imageId ?? uploadedImageId,
        title: description || response.title,
        description: description || response.description,
      });
    } catch (error) {
      enqueueSnackbar(`Error uploading image: ${getErrorMessage(error, 'Unknown error')}`, { variant: 'error' });
      reportError(error);
      throw error;
    }
  };

  const handleDelete = async (imageId: string | number) => {
    if (!onDeleteImage) {
      return;
    }

    await Promise.resolve(onDeleteImage(imageId));
  };

  const handleSaveImages = async () => {
    setSaveButtonState(buttonStates.saving);

    try {
      const uploadResult = await imageEditorRef.current?.uploadAllPending?.();

      if (uploadResult?.failedCount) {
        setSaveButtonState(buttonStates.failed);
        return;
      }

      debouncedPersistImages.cancel();
      await Promise.resolve(onUpdateImages(imagesRef.current));
      setSaveButtonState(buttonStates.saved);
    } catch {
      setSaveButtonState(buttonStates.failed);
    }
  };

  return (
    <Box mt={1} className="T-images-form">
      {shouldShowSaveButton ? (
        <Box>
          <Button disabled={isButtonStateSaving} variant="contained" onClick={handleSaveImages}>
            {buttonMessages[saveButtonState]}
          </Button>
        </Box>
      ) : null}

      <FlatImageEditor
        ref={imageEditorRef}
        deleteActionLabel="Delete image"
        enablePendingCropUpload={enablePendingCropUpload}
        items={flatItems}
        onPendingUploadStateChange={onPendingUploadStateChange}
        showDeleteAction={!hideDeleteButton}
        showDescriptionField
        showHiddenToggle={hasHiddenToggle}
        uploadLabel="Upload images"
        onDelete={handleDelete}
        onItemsChange={handleItemsChange}
        onUploadFile={handleUploadFile}
      />
    </Box>
  );
});

ImagesForm.displayName = 'ImagesForm';

export default ImagesForm;
